// Local TeleOp Component updated to mirror TeleOp.js UI/layout
import React, {useEffect, useRef, useState} from 'react';
import styles from './TeleOp.module.css';
import VideoFeed from './VideoFeed';
import KeyboardControls from './KeyboardControls';
import ControlsPanel from './ControlsPanel';
import {Link} from 'react-router-dom';

import useBackgroundColorChange from '../../utils/useBackgroundColorChange';

// Local teleop
const TeleOpLocal = () => {
    // UI/layout state (mirrors TeleOp.js)
    const [isChatOpen, setIsChatOpen] = useState(true);
    const [isConnected, setIsConnected] = useState(false);
    const [videoSrc, setVideoSrc] = useState(null);
    const [videoSrc2, setVideoSrc2] = useState(null);
    const [fps, setFps] = useState(0);
    // Local mode defaults to active teleop and no queue
    const [isTeleOpMode, setIsTeleOpMode] = useState(true);
    const [isControlConnected, setIsControlConnected] = useState(false);
    const [isControlPanelOpen, setIsControlPanelOpen] = useState(true);
    const [pressedKeysUI, setPressedKeysUI] = useState(new Set());

    // WebSocket refs
    const wsRef = useRef(null);   // main camera (8001)
    const ws2Ref = useRef(null);  // second camera (8002)
    const controlWsRef = useRef(null); // control (8000)
    const isIntentionalDisconnectRef = useRef(false);

    // Input handling refs
    const frameCountRef = useRef(0);
    const lastFpsUpdateRef = useRef(Date.now());
    const keysPressed = useRef(new Set());
    const sliderKeysPressed = useRef(new Set());
    const pollIntervalRef = useRef(null);

    // Keyboard mapping and speeds configuration (from TeleOp.js)
    const KEYMAP_PLUS = {
        x: 'KeyW',
        y: 'KeyA',
        z: 'KeyQ',
        t: 'ArrowUp',
        r: 'ArrowLeft',
        g: 'KeyX'
    };

    const KEYMAP_MINUS = {
        x: 'KeyS',
        y: 'KeyD',
        z: 'KeyE',
        t: 'ArrowDown',
        r: 'ArrowRight',
        g: 'KeyZ'
    };

    const AXIS_SPEEDS = {
        x: 2,
        y: 2,
        z: 2,
        t: 0.005,
        r: 0.01,
        g: 0.05
    };

    const POLL_RATE = 60;

    useBackgroundColorChange();  // change the background color for the new design


    // Connect to control WebSocket when in TeleOp mode
    useEffect(() => {
        if (isTeleOpMode) {
            connectControlWebSocket();
        } else {
            disconnectControlWebSocket();
        }

        return () => {
            disconnectControlWebSocket();
        };
    }, [isTeleOpMode]);

    const connectControlWebSocket = () => {
        try {
            controlWsRef.current = new WebSocket('ws://localhost:8000');

            controlWsRef.current.onopen = () => {
                setIsControlConnected(true);
                startKeyboardPolling();
            };

            controlWsRef.current.onerror = (error) => {
                console.error('Control WebSocket error:', error);
                setIsControlConnected(false);
            };

            controlWsRef.current.onclose = () => {
                setIsControlConnected(false);
                stopKeyboardPolling();
            };
        } catch (error) {
            console.error('Failed to connect to control WebSocket:', error);
            setIsControlConnected(false);
        }
    };

    const disconnectControlWebSocket = () => {
        if (controlWsRef.current) {
            controlWsRef.current.close();
            controlWsRef.current = null;
        }
        stopKeyboardPolling();
    };

    const startKeyboardPolling = () => {
        pollIntervalRef.current = setInterval(() => {
            if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                sendKeyboardData();
            }
        }, 1000 / POLL_RATE);
    };

    const stopKeyboardPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const sendKeyboardData = () => {
        const delta = {};
        let pressed = false;

        // Initialize all axes to 0
        ['x', 'y', 'z', 't', 'r', 'g'].forEach(axis => {
            delta[axis] = 0;

            // Check positive direction
            if (keysPressed.current.has(KEYMAP_PLUS[axis])) {
                delta[axis] += AXIS_SPEEDS[axis];
                pressed = true;
            }

            // Check negative direction
            if (keysPressed.current.has(KEYMAP_MINUS[axis])) {
                delta[axis] -= AXIS_SPEEDS[axis];
                pressed = true;
            }
        });

        // Always send movement delta, even if all zeros
        if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
            controlWsRef.current.send(JSON.stringify(delta));
            if (pressed) {
                // console.log('Sending movement data:', delta);
            }
        }
    };

    // Keyboard event handlers (mirrors TeleOp.js, without inactivity timers)
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isTeleOpMode) {
                // Prevent default behavior for all control keys when in teleop mode
                const controlKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'KeyX', 'KeyZ', 'KeyC', 'KeyV'];
                if (controlKeys.includes(event.code)) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                // Handle slider keys (C, V) separately - one-time trigger
                if (event.code === 'KeyC' && !sliderKeysPressed.current.has('KeyC')) {
                    sliderKeysPressed.current.add('KeyC');
                    setPressedKeysUI(prev => new Set([...prev, event.code]));
                    // Send slider command immediately
                    if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                        controlWsRef.current.send(JSON.stringify({'c': true}));
                    }
                    return;
                }

                if (event.code === 'KeyV' && !sliderKeysPressed.current.has('KeyV')) {
                    sliderKeysPressed.current.add('KeyV');
                    setPressedKeysUI(prev => new Set([...prev, event.code]));
                    // Send slider command immediately
                    if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                        controlWsRef.current.send(JSON.stringify({'v': true}));
                    }
                    return;
                }

                // Handle regular movement keys
                if (!keysPressed.current.has(event.code)) {
                    keysPressed.current.add(event.code);
                    // Update UI state
                    setPressedKeysUI(prev => new Set([...prev, event.code]));
                }
            }
        };

        const handleKeyUp = (event) => {
            if (isTeleOpMode) {
                // Prevent default behavior for all control keys when in teleop mode
                const controlKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'KeyX', 'KeyZ', 'KeyC', 'KeyV'];
                if (controlKeys.includes(event.code)) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }

            // Handle slider keys - send stop command
            if (event.code === 'KeyC' || event.code === 'KeyV') {
                sliderKeysPressed.current.delete(event.code);
                // Send slider stop command when releasing C or V
                if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                    controlWsRef.current.send(JSON.stringify({'slider': 'stop'}));
                }
            } else {
                keysPressed.current.delete(event.code);
            }

            // Update UI state
            setPressedKeysUI(prev => {
                const newSet = new Set(prev);
                newSet.delete(event.code);
                return newSet;
            });
        };

        // Prevent scrolling with arrow keys when in teleop mode
        const preventScroll = (event) => {
            if (isTeleOpMode && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
                event.preventDefault();
                return false;
            }
        };

        // Add event listeners
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('keydown', preventScroll, {passive: false});

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('keydown', preventScroll);
            keysPressed.current.clear();
            sliderKeysPressed.current.clear();
            setPressedKeysUI(new Set());
        };
    }, [isTeleOpMode]);

    // Simulate key press for virtual keyboard buttons
    const handleVirtualKeyPress = (keyCode) => {
        if (isTeleOpMode) {
            // Handle slider keys (C, V) separately - one-time trigger
            if (keyCode === 'KeyC' && !sliderKeysPressed.current.has('KeyC')) {
                sliderKeysPressed.current.add('KeyC');
                setPressedKeysUI(prev => new Set([...prev, keyCode]));
                if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                    controlWsRef.current.send(JSON.stringify({'c': true}));
                }
                return;
            }

            if (keyCode === 'KeyV' && !sliderKeysPressed.current.has('KeyV')) {
                sliderKeysPressed.current.add('KeyV');
                setPressedKeysUI(prev => new Set([...prev, keyCode]));
                if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                    controlWsRef.current.send(JSON.stringify({'v': true}));
                }
                return;
            }

            if (!keysPressed.current.has(keyCode)) {
                keysPressed.current.add(keyCode);
                setPressedKeysUI(prev => new Set([...prev, keyCode]));
            }
        }
    };

    // Simulate key release for virtual keyboard buttons
    const handleVirtualKeyRelease = (keyCode) => {
        if (keyCode === 'KeyC' || keyCode === 'KeyV') {
            sliderKeysPressed.current.delete(keyCode);
            if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                controlWsRef.current.send(JSON.stringify({'slider': 'stop'}));
            }
        } else {
            keysPressed.current.delete(keyCode);
        }

        setPressedKeysUI(prev => {
            const newSet = new Set(prev);
            newSet.delete(keyCode);
            return newSet;
        });
    };

    // Video WebSocket connection - only connect when in local teleop
    useEffect(() => {
        const connectWebSocket = () => {
            try {
                // First/main camera
                wsRef.current = new WebSocket('ws://localhost:8001');
                // Second camera (optional)
                ws2Ref.current = new WebSocket('ws://localhost:8002');

                wsRef.current.onopen = () => {
                    setIsConnected(true);
                    wsRef.current.send(JSON.stringify({type: 'ping'}));
                };

                wsRef.current.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);

                        if (data.type === 'video_frame') {
                            setVideoSrc(`data:image/jpeg;base64,${data.data}`);
                            frameCountRef.current++;
                            const now = Date.now();
                            const elapsed = now - lastFpsUpdateRef.current;
                            if (elapsed >= 1000) {
                                setFps(Math.round((frameCountRef.current * 1000) / elapsed));
                                frameCountRef.current = 0;
                                lastFpsUpdateRef.current = now;
                            }
                        } else if (data.type === 'pong') {
                            // no-op
                        }
                    } catch (error) {
                        console.error('Error parsing message from WebSocket 1:', error);
                    }
                };

                wsRef.current.onerror = (error) => {
                    console.error('WebSocket 1 error:', error);
                    setIsConnected(false);
                };

                wsRef.current.onclose = () => {
                    setIsConnected(false);
                    setVideoSrc(null);
                    // Only reconnect if it's not an intentional disconnect and still in teleop mode
                    if (isTeleOpMode && !isIntentionalDisconnectRef.current) {
                        setTimeout(connectWebSocket, 3000);
                    }
                };

                // Second WebSocket
                ws2Ref.current.onopen = () => {
                    ws2Ref.current.send(JSON.stringify({type: 'ping'}));
                };

                ws2Ref.current.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (data.type === 'video_frame') {
                            setVideoSrc2(`data:image/jpeg;base64,${data.data}`);
                        }
                    } catch (error) {
                        console.error('Error parsing message from WebSocket 2:', error);
                    }
                };

                ws2Ref.current.onerror = (error) => {
                    console.error('WebSocket 2 error:', error);
                };

                ws2Ref.current.onclose = () => {
                    setVideoSrc2(null);
                };
            } catch (error) {
                console.error('Failed to connect:', error);
                setIsConnected(false);
                if (isTeleOpMode && !isIntentionalDisconnectRef.current) {
                    setTimeout(connectWebSocket, 3000);
                }
            }
        };

        if (isTeleOpMode) {
            isIntentionalDisconnectRef.current = false;
            connectWebSocket();
        } else {
            if (wsRef.current) {
                isIntentionalDisconnectRef.current = true;
                wsRef.current.close();
                wsRef.current = null;
                setIsConnected(false);
                setVideoSrc(null);
            }
            if (ws2Ref.current) {
                ws2Ref.current.close();
                ws2Ref.current = null;
                setVideoSrc2(null);
            }
        }

        return () => {
            if (wsRef.current) {
                isIntentionalDisconnectRef.current = true;
                wsRef.current.close();
            }
            if (ws2Ref.current) {
                ws2Ref.current.close();
            }
        };
    }, [isTeleOpMode]);

    // Periodic ping
    useEffect(() => {
        const pingInterval = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({type: 'ping'}));
            }
            if (ws2Ref.current && ws2Ref.current.readyState === WebSocket.OPEN) {
                ws2Ref.current.send(JSON.stringify({type: 'ping'}));
            }
        }, 30000);

        return () => clearInterval(pingInterval);
    }, []);

    const handleEndTeleOp = () => {
        // Close control WS
        if (controlWsRef.current) {
            controlWsRef.current.close();
            controlWsRef.current = null;
        }
        // Close video WSs
        if (wsRef.current) {
            isIntentionalDisconnectRef.current = true;
            wsRef.current.close();
            wsRef.current = null;
            setIsConnected(false);
            setVideoSrc(null);
        }
        if (ws2Ref.current) {
            ws2Ref.current.close();
            ws2Ref.current = null;
            setVideoSrc2(null);
        }
        stopKeyboardPolling();
        setIsControlConnected(false);
        // Optionally exit teleop mode locally
        setIsTeleOpMode(false);
        keysPressed.current.clear();
        sliderKeysPressed.current.clear();
        setPressedKeysUI(new Set());
    };

    return (
        <div className={`${styles.container} ${styles.globalContainer}`}>
            <div className={`${styles.mainContent} ${(!isControlPanelOpen && isTeleOpMode) ? styles.mainContentExpanded : ''}`}>
                <div className={styles.navBar}>
                    <Link to="/live-control" className={styles.navLink}>
                        Live&nbsp;Control
                    </Link>
                    <span style={{marginInline: '10px'}}>&nbsp;|&nbsp;</span>
                    <span className={styles.activeNavBar}>Robotic&nbsp;Arm&nbsp;Control</span>
                </div>

                <VideoFeed
                    isConnected={isConnected}
                    isControlConnected={isControlConnected}
                    isTeleOpMode={isTeleOpMode}
                    videoSrc={videoSrc}
                    videoSrc2={videoSrc2}
                />

                <KeyboardControls
                    keysPressed={pressedKeysUI}
                    isControlConnected={isControlConnected}
                    onVirtualKeyPress={handleVirtualKeyPress}
                    onVirtualKeyRelease={handleVirtualKeyRelease}
                />
            </div>

            {isTeleOpMode && (
                <ControlsPanel
                    isControlConnected={isControlConnected}
                    onEndTeleOp={handleEndTeleOp}
                    isControlPanelOpen={isControlPanelOpen}
                    setIsControlPanelOpen={setIsControlPanelOpen}
                    remainingTime={0}
                />
            )}

            {!isChatOpen && !isTeleOpMode && (
                <button
                    className={styles.openChatBtn}
                    onClick={() => setIsChatOpen(true)}
                >
                    Open Chat
                </button>
            )}
        </div>
    );
};

export default TeleOpLocal;
